<template>
  <div class="time-cost-report">
    <!-- Layout com filtros na direita e relatório na esquerda -->
    <b-row class="h-100 no-gutters">
      <!-- Coluna da esquerda - Relatório -->
      <b-col cols="9" class="h-100">
        <b-card v-if="isMasterUser" id="report" no-body class="h-100">
          <div class="report-print-root" :class="printRootClass">
            <ReportPrintHeader
              v-if="printing"
              :title="printTitle"
              :subtitle="`Relatório de tempo e custo · Total: ${totalFilteredCount}`"
              :logo="printLogo"
              :generated-at="formatPrintDateTime(printGeneratedAt)"
              :company="printCompany"
              :period="printPeriod"
              :emitter="printEmitter"
            />
            <b-card-body class="position-relative">
              <div
                class="d-flex justify-content-between align-items-center mb-3 report-screen-heading"
              >
                <h2>{{ reportTitle }}</h2>
                <b-badge variant="info" pill class="p-2">
                  <i class="fas fa-file-alt mr-1"></i>
                  Total: {{ totalFilteredCount }}
                </b-badge>
              </div>

              <div class="complaints-layout-surface">
                <div v-if="loadingReport" class="skeleton-overlay">
                  <div class="table-responsive">
                    <table class="table complaints-layout-table skeleton-table">
                      <thead class="complaints-layout-header">
                        <tr>
                          <th
                            v-for="field in activeFields"
                            :key="field.key"
                            class="text-center align-middle"
                          >
                            {{ field.label }}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="i in 8" :key="i">
                          <td v-for="field in activeFields" :key="field.key">
                            <b-skeleton
                              :width="
                                field.key === 'protocol'
                                  ? '120px'
                                  : field.key === 'details'
                                  ? '90px'
                                  : '90%'
                              "
                            ></b-skeleton>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div
                  v-if="!loadingReport && reportItems.length === 0"
                  class="complaints-layout-empty text-center"
                >
                  <p>Nenhuma denúncia com tempo/custo encontrada.</p>
                </div>

                <b-table
                  v-show="!loadingReport && reportItems.length > 0"
                  borderless
                  hover
                  :responsive="!printing"
                  :items="reportItems"
                  :fields="printFields"
                  :filter="currentFilter"
                  :filter-function="filterResults"
                  table-class="complaints-layout-table"
                  thead-class="complaints-layout-header"
                  :tbody-tr-class="getReportRowClass"
                  @filtered="onFiltered"
                >
                  <template #cell(protocol)="data">
                    <b-button
                      v-if="!printing"
                      variant="link"
                      class="p-0 complaints-layout-link"
                      @click="goToComplaint(data.item._id)"
                    >
                      {{ data.value }}
                    </b-button>
                    <span v-else class="complaints-layout-link">
                      {{ data.value }}
                    </span>
                  </template>
                  <template #cell(status)="data">
                    <div class="status-cell">
                      <b-badge :variant="getStatusVariant(data.value)">
                        {{ getStatusText(data.value) }}
                      </b-badge>
                    </div>
                  </template>
                  <template #cell(totalTimeSpent)="data">
                    <div class="time-cell">
                      {{ formatTime(data.value) }}
                    </div>
                  </template>
                  <template #cell(totalCost)="data">
                    <div class="cost-cell">
                      R$ {{ formatCurrency(data.value) }}
                    </div>
                  </template>
                  <template #cell(companyName)="data">
                    {{ data.value }}
                  </template>
                  <template #cell(details)="data">
                    <b-button
                      v-if="!printing"
                      variant="info"
                      size="sm"
                      @click="showTimeCostModal(data.item)"
                    >
                      <i class="fas fa-eye"></i> Detalhes
                    </b-button>
                  </template>
                </b-table>
              </div>
            </b-card-body>
          </div>
        </b-card>

        <b-card v-else id="report" class="h-100">
          <div class="report-print-root" :class="printRootClass">
            <ReportPrintHeader
              v-if="printing"
              :title="printTitle"
              :subtitle="`Relatório de tempo e custo · Total: ${totalFilteredCount}`"
              :logo="printLogo"
              :generated-at="formatPrintDateTime(printGeneratedAt)"
              :company="printCompany"
              :period="printPeriod"
              :emitter="printEmitter"
            />
            <div
              class="d-flex justify-content-between align-items-center mb-3 report-screen-heading"
            >
              <h2>{{ reportTitle }}</h2>
              <b-badge variant="info" pill class="p-2">
                <i class="fas fa-file-alt mr-1"></i>
                Total: {{ totalFilteredCount }}
              </b-badge>
            </div>
            <b-card-body class="position-relative">
              <div class="complaints-layout-surface">
                <div v-if="loadingReport" class="skeleton-overlay">
                  <div class="table-responsive">
                    <table class="table complaints-layout-table skeleton-table">
                      <thead class="complaints-layout-header">
                        <tr>
                          <th v-for="field in activeFields" :key="field.key">
                            {{ field.label }}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="i in 8" :key="i">
                          <td v-for="field in activeFields" :key="field.key">
                            <b-skeleton
                              :width="
                                field.key === 'protocol'
                                  ? '120px'
                                  : field.key === 'details'
                                  ? '90px'
                                  : '90%'
                              "
                            ></b-skeleton>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div
                  v-if="!loadingReport && reportItems.length === 0"
                  class="complaints-layout-empty text-center"
                >
                  <p>Nenhuma denúncia com tempo/custo encontrada.</p>
                </div>

                <b-table
                  v-show="!loadingReport && reportItems.length > 0"
                  borderless
                  hover
                  :responsive="!printing"
                  :filter="currentFilter"
                  :filter-function="filterResults"
                  :items="reportItems"
                  :fields="printFields"
                  class="force-printing-overflow"
                  table-class="complaints-layout-table"
                  thead-class="complaints-layout-header"
                  :tbody-tr-class="getReportRowClass"
                  @filtered="onFiltered"
                >
                  <template #cell(protocol)="data">
                    <b-button
                      v-if="!printing"
                      variant="link"
                      class="p-0 complaints-layout-link"
                      @click="goToComplaint(data.item._id)"
                    >
                      {{ data.value }}
                    </b-button>
                    <span v-else class="complaints-layout-link">
                      {{ data.value }}
                    </span>
                  </template>
                  <template #cell(status)="data">
                    <div class="status-cell">
                      <b-badge :variant="getStatusVariant(data.value)">
                        {{ getStatusText(data.value) }}
                      </b-badge>
                    </div>
                  </template>
                  <template #cell(totalTimeSpent)="data">
                    <div class="time-cell">
                      {{ formatTime(data.value) }}
                    </div>
                  </template>
                  <template #cell(totalCost)="data">
                    <div class="cost-cell">
                      R$ {{ formatCurrency(data.value) }}
                    </div>
                  </template>
                  <template #cell(companyName)="data">
                    {{ data.value }}
                  </template>
                  <template #cell(details)="data">
                    <b-button
                      v-if="!printing"
                      variant="info"
                      size="sm"
                      @click="showTimeCostModal(data.item)"
                    >
                      <i class="fas fa-eye"></i> Detalhes
                    </b-button>
                  </template>
                </b-table>
              </div>
            </b-card-body>
          </div>
        </b-card>
      </b-col>
      <!-- Coluna da direita - Filtros -->
      <b-col cols="3" class="pl-3">
        <b-card header="Parâmetros" class="filters-sidebar h-100">
          <div class="filters-container">
            <!-- Filtro de Empresa (apenas para root ou company-root gestora) -->
            <div v-if="showCompanyFilter" class="filter-group">
              <div class="filter-header" @click="toggleSection('empresa')">
                <h6 class="filter-title">Empresa</h6>
                <b-button variant="link" size="sm" class="collapse-btn p-0">
                  <i
                    :class="
                      collapsedSections.empresa
                        ? 'fas fa-chevron-down'
                        : 'fas fa-chevron-up'
                    "
                  />
                </b-button>
              </div>
              <b-collapse
                id="collapse-empresa"
                :visible="!collapsedSections.empresa"
              >
                <b-form-group label="Selecionar Empresa" class="mb-3">
                  <b-form-select
                    v-model="filter.selectedCompany"
                    :options="companyOptions"
                    @change="onFilterChange"
                  />
                </b-form-group>
              </b-collapse>
            </div>

            <!-- Filtro de Data -->
            <div class="filter-group">
              <div class="filter-header" @click="toggleSection('data')">
                <h6 class="filter-title">Período</h6>
                <b-button variant="link" size="sm" class="collapse-btn p-0">
                  <i
                    :class="
                      collapsedSections.data
                        ? 'fas fa-chevron-down'
                        : 'fas fa-chevron-up'
                    "
                  />
                </b-button>
              </div>
              <b-collapse id="collapse-data" :visible="!collapsedSections.data">
                <b-form-group label="Data inicial" class="mb-3">
                  <b-form-input
                    v-model="filter.startDate"
                    type="date"
                    @input="onFilterChange"
                  />
                </b-form-group>
                <b-form-group label="Data final" class="mb-3">
                  <b-form-input
                    v-model="filter.endDate"
                    type="date"
                    @input="onFilterChange"
                  />
                </b-form-group>
              </b-collapse>
            </div>

            <!-- Filtro de Status -->
            <div class="filter-group">
              <div class="filter-header" @click="toggleSection('status')">
                <h6 class="filter-title">Status da Denúncia</h6>
                <b-button variant="link" size="sm" class="collapse-btn p-0">
                  <i
                    :class="
                      collapsedSections.status
                        ? 'fas fa-chevron-down'
                        : 'fas fa-chevron-up'
                    "
                  />
                </b-button>
              </div>
              <b-collapse
                id="collapse-status"
                :visible="!collapsedSections.status"
              >
                <b-form-checkbox-group
                  v-model="filter.status"
                  :options="statusOpts"
                  stacked
                  class="mb-3"
                  @change="onFilterChange"
                />
              </b-collapse>
            </div>

            <!-- Filtro de Tempo Mínimo -->
            <div class="filter-group">
              <div class="filter-header" @click="toggleSection('tempo')">
                <h6 class="filter-title">Tempo Mínimo (minutos)</h6>
                <b-button variant="link" size="sm" class="collapse-btn p-0">
                  <i
                    :class="
                      collapsedSections.tempo
                        ? 'fas fa-chevron-down'
                        : 'fas fa-chevron-up'
                    "
                  />
                </b-button>
              </div>
              <b-collapse
                id="collapse-tempo"
                :visible="!collapsedSections.tempo"
              >
                <b-form-group label="Tempo mínimo" class="mb-3">
                  <b-form-input
                    v-model="filter.minTime"
                    type="number"
                    min="0"
                    placeholder="Ex: 30"
                    @input="onFilterChange"
                  />
                </b-form-group>
              </b-collapse>
            </div>

            <!-- Filtro de Custo Mínimo -->
            <div class="filter-group">
              <div class="filter-header" @click="toggleSection('custo')">
                <h6 class="filter-title">Custo Mínimo (R$)</h6>
                <b-button variant="link" size="sm" class="collapse-btn p-0">
                  <i
                    :class="
                      collapsedSections.custo
                        ? 'fas fa-chevron-down'
                        : 'fas fa-chevron-up'
                    "
                  />
                </b-button>
              </div>
              <b-collapse
                id="collapse-custo"
                :visible="!collapsedSections.custo"
              >
                <b-form-group label="Custo mínimo" class="mb-3">
                  <b-form-input
                    v-model="filter.minCost"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="Ex: 100.00"
                    @input="onFilterChange"
                  />
                </b-form-group>
              </b-collapse>
            </div>

            <!-- Botões de ação -->
            <div class="d-flex flex-column mt-3">
              <b-button
                variant="secondary"
                class="mb-2"
                @click="clearParameters"
              >
                <i class="fas fa-trash"></i> Limpar
              </b-button>
              <b-button variant="primary" @click="print">
                <i class="fas fa-print"></i> Imprimir
              </b-button>
              <b-button variant="success" class="mt-2" @click="exportCSV">
                <i class="fas fa-file-csv"></i> Exportar CSV
              </b-button>
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <!-- Modal com detalhes do tempo/custo -->
    <b-modal
      v-model="showModal"
      title="Detalhes do Tempo e Custo"
      size="xl"
      scrollable
      centered
      hide-footer
    >
      <div v-if="selectedComplaint && selectedComplaint.timeCostEvents">
        <div class="d-flex justify-content-between align-items-center mb-4">
          <h5 class="mb-0">Protocolo: {{ selectedComplaint.protocol }}</h5>
          <div class="totals-info">
            <strong>Total Tempo:</strong>
            {{ formatTime(selectedComplaint.totalTimeSpent) }} |
            <strong>Total Custo:</strong> R$
            {{ formatCurrency(selectedComplaint.totalCost) }}
          </div>
        </div>

        <b-table
          striped
          hover
          responsive
          :items="selectedComplaint.timeCostEvents"
          :fields="timeCostFields"
          table-class="modern-table"
        >
          <template #cell(createdAt)="data">
            <span>{{ formatDate(data.value) }}</span>
          </template>
          <template #cell(timeSpent)="data">
            <span>{{ data.value }} minutos</span>
          </template>
          <template #cell(cost)="data">
            <span>R$ {{ formatCurrency(data.value) }}</span>
          </template>
          <template #cell(userName)="data">
            <span>{{ data.value || "Usuário não encontrado" }}</span>
          </template>
        </b-table>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { timeCostReport } from "@/api/reports";
import { getStatusText } from "../utils";
import ComplaintStatus from "../../../../../backend/models/ComplaintStatus.ts";
import moment from "moment";
import { getCompanyUsers, getCompanies, getCompanyInfo } from "@/api/company";
import ReportPrintHeader from "@/components/ReportPrintHeader.vue";
import reportPrintMixin from "@/mixins/reportPrint";

export default {
  name: "TimeCostReport",
  components: {
    ReportPrintHeader,
  },
  mixins: [reportPrintMixin],
  data() {
    const statusOpts = [];
    for (const status of Object.keys(ComplaintStatus)) {
      const text = getStatusText(ComplaintStatus[status]);
      statusOpts.push({ value: ComplaintStatus[status], text });
    }

    return {
      totalRows: 0,
      loadingReport: false,
      statusOpts,
      filter: {
        status: [],
        minTime: null,
        minCost: null,
        selectedCompany: null,
        startDate: null,
        endDate: null,
      },
      currentFilter: {
        status: [],
        minTime: null,
        minCost: null,
        selectedCompany: null,
        startDate: null,
        endDate: null,
      },
      report: { complaints: [] },
      fields: [
        { key: "protocol", label: "Protocolo", sortable: true },
        { key: "status", label: "Status", sortable: true },
        { key: "totalTimeSpent", label: "Tempo Total", sortable: true },
        { key: "totalCost", label: "Custo Total", sortable: true },
        { key: "details", label: "Detalhes" },
      ],
      companyUsers: {},
      availableCompanies: [],
      collapsedSections: {
        empresa: false,
        status: false,
        tempo: false,
        custo: true,
        data: false,
      },
      showModal: false,
      selectedComplaint: null,
      timeCostFields: [
        {
          key: "createdAt",
          label: "Data",
          sortable: true,
          thClass: "text-center align-middle",
          tdClass: "text-center align-middle",
        },
        {
          key: "timeSpent",
          label: "Tempo Gasto",
          sortable: true,
          thClass: "text-center align-middle",
          tdClass: "text-center align-middle",
        },
        {
          key: "cost",
          label: "Custo",
          sortable: true,
          thClass: "text-center align-middle",
          tdClass: "text-center align-middle",
        },
        {
          key: "description",
          label: "Descrição",
          sortable: false,
          thClass: "align-middle",
          tdClass: "align-middle",
        },
        {
          key: "userName",
          label: "Usuário",
          sortable: true,
          thClass: "text-center align-middle",
          tdClass: "text-center align-middle",
        },
      ],
    };
  },
  computed: {
    ...mapGetters(["profile", "classificationOptions", "classificationMaps"]),
    isMasterUser() {
      return this.profile.role === "root" || this.isCompanyManager;
    },
    isCompanyManager() {
      return (
        this.profile.role === "company-root" &&
        this.profile.managedCompanies &&
        this.profile.managedCompanies.length > 0
      );
    },
    showCompanyFilter() {
      return this.profile.role === "root" || this.isCompanyManager;
    },
    companyUsersList() {
      const users = [];
      for (const user of Object.values(this.companyUsers)) {
        users.push({ value: user._id, text: `${user.name} ${user.surname}` });
      }
      return users;
    },
    companyOptions() {
      const options = [{ value: null, text: "Todas as Empresas" }];

      if (this.availableCompanies && this.availableCompanies.length > 0) {
        this.availableCompanies.forEach((company) => {
          options.push({
            value: company._id,
            text: company.fantasyName || company.name,
          });
        });
      }

      return options;
    },
    showCompanyColumn() {
      return this.isMasterUser && !this.currentFilter.selectedCompany;
    },
    activeFields() {
      if (!this.showCompanyColumn) {
        return this.fields;
      }

      const [firstField, ...remainingFields] = this.fields;

      return [
        firstField,
        { key: "companyName", label: "Empresa", sortable: true },
        ...remainingFields,
      ];
    },
    reportItems() {
      if (this.isMasterUser) {
        if (!Array.isArray(this.report)) return [];

        return this.report.flatMap((item) => {
          const companyName =
            item.company?.fantasyName || item.company?.name || "N/A";

          return (item.complaints || []).map((complaint) => ({
            ...complaint,
            companyName,
          }));
        });
      }

      return (this.report.complaints || []).map((complaint) => ({
        ...complaint,
        companyName:
          this.report.company?.fantasyName || this.report.company?.name || "",
      }));
    },
    reportTitle() {
      if (!this.isMasterUser) {
        return `Relatório de Tempo e Custo da ${
          this.report.company ? this.report.company.fantasyName : "Empresa"
        }`;
      }

      if (this.currentFilter.selectedCompany) {
        const selectedCompany = this.companyOptions.find(
          (company) => company.value === this.currentFilter.selectedCompany
        );

        return `Relatório de Tempo e Custo - ${
          selectedCompany ? selectedCompany.text : "Empresa Selecionada"
        }`;
      }

      return "Relatório de Tempo e Custo";
    },
    printTitle() {
      return this.reportTitle;
    },
    totalFilteredCount() {
      return this.reportItems.filter((row) =>
        this.filterResults(row, this.currentFilter)
      ).length;
    },
  },
  async created() {
    await this.loadCompanyPrintLogo(this.profile.company);
    await this.loadCompanies();
    await this.loadData();
  },
  methods: {
    getStatusText,
    getReportRowClass(item) {
      if (!item) {
        return "";
      }

      const baseClass = "complaints-layout-row";
      switch (item.status) {
        case ComplaintStatus.NEW:
          return `${baseClass} complaints-layout-row--new`;
        case ComplaintStatus.CONCLUDED:
        case ComplaintStatus.DONE:
          return `${baseClass} complaints-layout-row--concluded`;
        default:
          return baseClass;
      }
    },
    formatDate(value) {
      if (!value) {
        return "—";
      }
      return moment(value).format("DD/MM/YYYY HH:mm");
    },
    formatTime(minutes) {
      if (!minutes) return "0 min";
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      if (hours > 0) {
        return `${hours}h ${mins}min`;
      }
      return `${mins}min`;
    },
    formatCurrency(value) {
      if (!value) return "0,00";
      // Valor já vem em reais do backend, não precisa dividir por 100
      return value.toFixed(2).replace(".", ",");
    },
    getStatusVariant(status) {
      const statusMap = {
        [ComplaintStatus.NEW]: "primary",
        [ComplaintStatus.REJECTED]: "dark",
        [ComplaintStatus.IN_PROGRESS]: "warning",
        [ComplaintStatus.PENDING]: "info",
        [ComplaintStatus.DONE]: "success",
        [ComplaintStatus.CONCLUDED]: "success",
        [ComplaintStatus.REOPENED]: "warning",
        [ComplaintStatus.FORWARDED]: "primary",
      };
      return statusMap[status] || "secondary";
    },
    getUserName(userId) {
      const user = this.companyUsers[userId];
      return user ? `${user.name} ${user.surname}` : "Usuário não encontrado";
    },
    clearParameters() {
      this.filter.status = [];
      this.filter.minTime = null;
      this.filter.minCost = null;
      this.filter.selectedCompany = null;
      this.filter.startDate = null;
      this.filter.endDate = null;

      this.currentFilter = {
        status: [],
        minTime: null,
        minCost: null,
        selectedCompany: null,
        startDate: null,
        endDate: null,
      };
    },
    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length;
    },
    onFilterChange() {
      this.currentFilter.status = this.filter.status;
      this.currentFilter.minTime = this.filter.minTime
        ? parseInt(this.filter.minTime)
        : null;
      this.currentFilter.minCost = this.filter.minCost
        ? parseFloat(this.filter.minCost)
        : null;
      this.currentFilter.selectedCompany = this.filter.selectedCompany;
      this.currentFilter.startDate = this.filter.startDate;
      this.currentFilter.endDate = this.filter.endDate;

      // Se a empresa foi alterada, recarregar os dados
      if (this.isMasterUser) {
        this.loadData(false);
      }
    },
    filterResults(row, filter) {
      // Filtro por status
      if (
        filter.status &&
        filter.status.length &&
        filter.status.indexOf(row.status) === -1
      ) {
        return false;
      }

      // Filtro por tempo mínimo
      if (filter.minTime && row.totalTimeSpent < filter.minTime) {
        return false;
      }

      // Filtro por custo mínimo
      if (filter.minCost && row.totalCost < filter.minCost) {
        return false;
      }

      return true;
    },

    exportCSV() {
      const escapeCsv = (value) => {
        if (value === null || value === undefined) return "";
        if (Array.isArray(value)) value = value.join(", ");
        if (typeof value === "object") value = JSON.stringify(value);
        value = String(value);
        const needsQuotes = /[",\n\r;]/.test(value);
        const escaped = value.replace(/"/g, '""');
        return needsQuotes ? `"${escaped}"` : escaped;
      };

      const formatValue = (field, row) => {
        switch (field.key) {
          case "status":
            return this.getStatusText(row.status);
          case "totalTimeSpent":
            return this.formatTime(row.totalTimeSpent);
          case "totalCost":
            return this.formatCurrency(row.totalCost);
          case "details":
            return ""; // Não incluir na exportação
          default:
            return row[field.key] == null ? "" : row[field.key];
        }
      };

      const headers = this.fields
        .filter((f) => f.key !== "details")
        .map((f) => f.label || f.key);
      const includeCompany = this.isMasterUser;
      const headerRow = includeCompany ? ["Empresa", ...headers] : headers;

      const allRows = [];
      const appendRows = (companyName, rows) => {
        for (const row of rows) {
          if (!this.filterResults(row, this.currentFilter)) continue;
          const csvRow = this.fields
            .filter((f) => f.key !== "details")
            .map((f) => formatValue(f, row));
          allRows.push(includeCompany ? [companyName, ...csvRow] : csvRow);
        }
      };

      if (this.isMasterUser) {
        for (const item of this.report) {
          const companyName =
            item.company?.fantasyName || item.company?.name || "";
          appendRows(companyName, item.complaints || []);
        }
      } else {
        const companyName =
          this.report.company?.fantasyName || this.report.company?.name || "";
        appendRows(companyName, this.report.complaints || []);
      }

      const separator = ";";
      const lines = [headerRow, ...allRows]
        .map((row) => row.map(escapeCsv).join(separator))
        .join("\r\n");

      const bom = "\ufeff"; // Excel-friendly BOM
      const blob = new Blob([bom + lines], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ts = moment().format("YYYYMMDD_HHmmss");
      a.href = url;
      a.download = `relatorio_tempo_custo_${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },

    toggleSection(section) {
      this.collapsedSections[section] = !this.collapsedSections[section];
    },

    showTimeCostModal(complaint) {
      this.selectedComplaint = complaint;
      this.showModal = true;
    },

    goToComplaint(complaintId) {
      this.$router.push({ path: `/denuncias/${complaintId}` });
    },

    async loadCompanies() {
      if (!this.profile || !this.isMasterUser) {
        return;
      }

      try {
        let companies = [];

        if (this.profile.role === "root") {
          // Usuário root vê todas as empresas
          const response = await getCompanies({ size: 1000, page: 1 });

          if (Array.isArray(response.data)) {
            companies = response.data;
          } else if (response.data && response.data.docs) {
            companies = response.data.docs;
          }
        } else if (this.profile.role === "company-root") {
          // Usuário company-root: construir lista baseada no profile
          const companyIds = [];

          // Adicionar a própria empresa
          if (this.profile.company) {
            companyIds.push(this.profile.company);
          }

          // Adicionar empresas gerenciadas
          if (
            this.profile.managedCompanies &&
            Array.isArray(this.profile.managedCompanies)
          ) {
            companyIds.push(...this.profile.managedCompanies);
          }

          // Buscar detalhes das empresas específicas
          if (companyIds.length > 0) {
            try {
              // Buscar detalhes de cada empresa usando a API pública
              const companyPromises = companyIds.map((id) =>
                getCompanyInfo(id)
              );
              const companyResponses = await Promise.all(companyPromises);

              companies = companyResponses.map((response, index) => ({
                _id: companyIds[index],
                fantasyName:
                  response.data.fantasyName ||
                  response.data.name ||
                  `Empresa ${companyIds[index]}`,
                name: response.data.name || `Empresa ${companyIds[index]}`,
              }));
            } catch (detailError) {
              console.error(
                "TimeCostReport: Erro ao buscar detalhes das empresas",
                detailError
              );
              // Fallback: criar objetos básicos
              companies = companyIds.map((id) => ({
                _id: id,
                fantasyName: `Empresa ${id}`,
                name: `Empresa ${id}`,
              }));
            }
          }
        }

        this.availableCompanies = companies;
      } catch (error) {
        console.error("TimeCostReport: Erro ao carregar empresas:", error);
        this.$bvToast.toast("Não foi possível carregar a lista de empresas.", {
          title: "Erro",
          toastClass: "custom-toast custom-toast-error",
          variant: "light",
          solid: true,
          autoHideDelay: 10000,
          noCloseButton: false,
        });
      }
    },

    async loadData(loadFullData = true) {
      this.loadingReport = true;

      try {
        if (this.isMasterUser) {
          let response;
          try {
            response = await timeCostReport(
              null,
              this.filter.startDate,
              this.filter.endDate
            );
          } catch (error) {
            console.error(
              "TimeCostReport: Erro na chamada da API para usuário master",
              error
            );
            if (error.response.status === 403) {
              this.$bvToast.toast(error.response.data, {
                title: "Erro",
                toastClass: "custom-toast custom-toast-error",
                variant: "light",
                solid: true,
                autoHideDelay: 10000,
                noCloseButton: false,
              });
            } else {
              this.$bvToast.toast(error.response.statusText, {
                title: "Erro",
                toastClass: "custom-toast custom-toast-error",
                variant: "light",
                solid: true,
                autoHideDelay: 10000,
                noCloseButton: false,
              });
            }
            return;
          }
          // Para usuários root, o backend retorna um array com dados de todas as empresas
          const allData = response.data || [];

          // Dados reais devem vir da API

          // Se uma empresa específica foi selecionada, filtrar apenas aquela empresa
          if (this.filter.selectedCompany) {
            const filteredData = allData.filter(
              (item) =>
                item.company && item.company._id === this.filter.selectedCompany
            );
            this.report = filteredData.length > 0 ? filteredData : [];
          } else {
            // Mostrar todas as empresas
            this.report = allData;
          }

          // Forçar reatividade dos dados
          this.$set(this, "report", this.report);
        } else {
          // Se loadFullData for false, apenas carregar o relatório sem recarregar classificações
          if (loadFullData) {
            try {
              response = await getCompanyUsers(this.profile.company, true);
              this.companyUsers = response.data.reduce((obj, user) => {
                obj[user["_id"]] = user;
                return obj;
              }, {});
            } catch (error) {
              console.error(
                "TimeCostReport: Erro ao carregar usuários da empresa:",
                error
              );
            }
          }

          // Sempre carregar o relatório
          let response;
          try {
            response = await timeCostReport(
              this.profile.company,
              this.filter.startDate,
              this.filter.endDate
            );
            this.report = response.data || { complaints: [], company: null };

            // Dados reais devem vir da API

            this.totalRows = this.report.complaints
              ? this.report.complaints.length
              : 0;
            // Forçar reatividade dos dados
            this.$set(this, "report", this.report);
          } catch (error) {
            console.error(
              "TimeCostReport: Erro na chamada da API para empresa específica",
              error
            );
            if (error.response.status === 403) {
              this.$bvToast.toast(error.response.data, {
                title: "Erro",
                toastClass: "custom-toast custom-toast-error",
                variant: "light",
                solid: true,
                autoHideDelay: 10000,
                noCloseButton: false,
              });
            } else {
              this.$bvToast.toast(error.response.statusText, {
                title: "Erro",
                toastClass: "custom-toast custom-toast-error",
                variant: "light",
                solid: true,
                autoHideDelay: 10000,
                noCloseButton: false,
              });
            }
            return;
          }
        }
      } finally {
        this.loadingReport = false;
      }
    },
  },
};
</script>

<style scoped>
.time-cost-report {
  height: 100vh;
  overflow: hidden;
}

.time-cost-report .row {
  height: 100%;
  margin: 0;
  align-items: stretch;
}

.time-cost-report .col-3,
.time-cost-report .col-9 {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.time-cost-report .col-9 {
  padding-right: 15px;
}

.time-cost-report .col-3 {
  padding-left: 15px;
}
.filters-sidebar {
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: #f8f9fa;
  border: none;
  border-radius: 8px;
}
/* Harmonize the report card style with the parameters card */
.time-cost-report #report.card {
  border: none;
  border-radius: 8px;
}
.filters-sidebar >>> .card-header {
  background-color: transparent;
  border-bottom: 1px solid #e9ecef;
  font-weight: 600;
  color: #343a40;
  font-size: 1.1rem;
}
.filters-sidebar .card-body {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.filters-container {
  flex: 1;
  overflow-y: auto;
  padding-right: 10px;
}

.time-cost-report .col-9 .card {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.time-cost-report .col-9 .card-body {
  flex: 1;
  overflow-y: auto;
}

.skeleton-overlay {
  position: relative;
}

.skeleton-table {
  border-spacing: 0;
}

.skeleton-table tbody tr {
  box-shadow: none;
}

.skeleton-table tbody td {
  background-color: #fff;
}

.filter-group {
  margin-bottom: 1.5rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #e9ecef;
}

.filter-group:last-child {
  border-bottom: none;
}

.filter-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  padding: 0;
  border-radius: 4px;
  transition: background-color 0.2s ease;
}

.filter-header:hover {
  background-color: transparent;
}

.filter-title {
  color: #495057;
  font-weight: 600;
  margin-bottom: 0.75rem;
  padding-bottom: 0;
  border-bottom: none;
  flex: 1;
  font-size: 0.9rem;
}

.collapse-btn {
  color: #6c757d;
  transition: color 0.2s ease, transform 0.2s ease;
  line-height: 1;
}

.collapse-btn:hover {
  color: #007bff;
  text-decoration: none;
}

.collapse-btn i {
  transition: transform 0.3s ease;
  font-size: 0.875rem;
}

.filter-header:hover .collapse-btn {
  color: #007bff;
}

/* Animação suave para o collapse */
.collapse {
  transition: all 0.3s ease;
}

.collapsing {
  transition: height 0.3s ease;
}

.form-control[multiple] {
  min-height: 80px;
}

/* Estilos para checkboxes e radio buttons */
.form-check {
  padding-left: 1.5rem;
  margin-bottom: 0.5rem;
}

.form-check-input {
  margin-left: -1.5rem;
}

.form-check-label {
  font-size: 0.875rem;
  line-height: 1.4;
  color: #495057;
  cursor: pointer;
  transition: color 0.2s ease;
}

.form-check-label:hover {
  color: #007bff;
}

.custom-checkbox .custom-control-input:checked ~ .custom-control-label::before {
  background-color: #007bff;
  border-color: #007bff;
}

.custom-radio .custom-control-input:checked ~ .custom-control-label::before {
  background-color: #007bff;
  border-color: #007bff;
}

/* Espaçamento entre grupos de checkboxes */
.filter-group .form-group:not(:last-child) {
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #f0f0f0;
}

/* Scroll suave para containers de checkboxes */
.filter-group {
  max-height: none;
}

.filter-group .form-check-input:focus {
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.mb-2 {
  margin-bottom: 0.5rem;
}

.mb-3 {
  margin-bottom: 1rem;
}

.h-100 {
  min-height: calc(100vh - 120px);
}

/* Customização dos scrollbars */
.filters-container::-webkit-scrollbar {
  width: 6px;
}

.filters-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.filters-container::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}

.filters-container::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.d-flex.flex-column.mt-3 .b-button {
  border-radius: 6px;
  padding: 0.6rem 1rem;
  font-weight: 500;
  transition: background-color 0.2s ease, transform 0.2s ease,
    box-shadow 0.2s ease;
}
.d-flex.flex-column.mt-3 .b-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}
.d-flex.flex-column.mt-3 .btn-secondary {
  background-color: #6c757d;
  border-color: #6c757d;
}
.d-flex.flex-column.mt-3 .btn-primary {
  background-color: #007bff;
  border-color: #007bff;
}
/* Responsividade */
@media (max-width: 1200px) {
  .time-cost-report {
    height: auto;
    min-height: 100vh;
  }

  .filters-sidebar {
    height: auto;
    min-height: 400px;
  }

  .filters-container {
    height: auto;
    max-height: 400px;
  }

  .time-cost-report .col-3,
  .time-cost-report .col-9 {
    height: auto;
  }
}

@media (max-width: 768px) {
  .time-cost-report .col-3 {
    flex: 0 0 100%;
    max-width: 100%;
    margin-bottom: 20px;
    height: auto;
    padding-left: 0;
  }

  .time-cost-report .col-9 {
    flex: 0 0 100%;
    max-width: 100%;
    height: auto;
    padding-right: 0;
  }

  .time-cost-report .row {
    height: auto;
    flex-direction: column;
  }
}

/* Estilos para células de status, tempo e custo */
.status-cell,
.time-cell,
.cost-cell {
  text-align: center;
  font-weight: 600;
}

/* Estilos para o modal */
.totals-info {
  background-color: #f8f9fa;
  padding: 0.75rem;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}
</style>
<style>
.modern-table {
  border-collapse: separate;
  border-spacing: 0;
  width: 100%;
}

.modern-table thead th {
  background-color: #eef1f4;
  border: none;
  color: #56606b;
  font-weight: 600;
  padding: 1rem;
  text-transform: uppercase;
  font-size: 0.8rem;
  letter-spacing: 0.5px;
}

.modern-table thead th:first-child {
  border-top-left-radius: 10px;
  border-bottom-left-radius: 10px;
}

.modern-table thead th:last-child {
  border-top-right-radius: 10px;
  border-bottom-right-radius: 10px;
}

.modern-table tbody tr {
  background-color: #fff;
}

.modern-table tbody tr:hover {
  background-color: #fbfcfe;
}

.modern-table tbody td {
  border-top: none;
  border-bottom: 1px solid #e6eaef;
  padding: 1rem;
  vertical-align: middle;
}

.modern-table tbody tr:hover td {
  background-color: #fbfcfe;
}
.status-badge {
  padding: 0.4em 0.8em;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #212529;
  white-space: nowrap;
}
.status-new {
  background-color: #fce4e4;
  color: #c0392b;
}
.status-in-progress {
  background-color: #e0e7ff;
  color: #3730a3;
}
.status-done {
  background-color: #d1fae5;
  color: #065f46;
}
.status-concluded {
  background-color: #d1fae5;
  color: #065f46;
}
.status-rejected {
  background-color: #e5e7eb;
  color: #4b5563;
}
.status-pending,
.status-forwarded,
.status-reopened,
.status-default {
  background-color: #e0e7ff;
  color: #3730a3;
}

/* Centralizar todas as colunas do relatório */
.modern-table td {
  text-align: center;
}

.modern-table th {
  text-align: center;
}

/* Estilos para o modal de detalhes */
.totals-info strong {
  color: #495057;
}

/* Estilos para o link do protocolo */
.btn-link.text-primary {
  text-decoration: none;
  transition: all 0.2s ease;
}

.btn-link.text-primary:hover {
  text-decoration: underline;
  transform: scale(1.05);
}

/* Centralização vertical das células da tabela */
.modern-table td.align-middle {
  vertical-align: middle !important;
}

.modern-table td {
  vertical-align: middle;
}

/* Centralização vertical dos cabeçalhos da tabela */
.modern-table th.align-middle {
  vertical-align: middle !important;
}

.modern-table th {
  vertical-align: middle;
}
</style>
